import { memo, useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Button,
  Card,
  HighlightCard,
  Skeleton,
  StatusMessage,
  useToast,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { getIntegrationProviderLogo } from '../../constants';
import { useProviderUsage } from '../../hooks/provider';
import { AlertTriangle, Bot, ExternalLink, LayoutGrid, Share2, Workflow } from 'lucide-react';

const IMPACT_TILES = [
  {
    key: 'workflows',
    icon: Workflow,
    variant: 'primary',
    helperKey: 'workflowsHelper',
    iconHighlight: { bgColor: 'Primary', iconColor: 'PrimaryAlt', size: 'small' },
  },
  {
    key: 'agents',
    icon: Bot,
    variant: 'purple',
    helperKey: 'agentsHelper',
    iconHighlight: { bgColor: 'Purple', iconColor: 'Purple', size: 'small' },
  },
  {
    key: 'networks',
    icon: Share2,
    variant: 'orange',
    helperKey: 'networksHelper',
    iconHighlight: { bgColor: 'Alert', iconColor: 'Alert', size: 'small' },
  },
  {
    key: 'widgets',
    icon: LayoutGrid,
    variant: 'green',
    helperKey: 'widgetsHelper',
    iconHighlight: { bgColor: 'Success', iconColor: 'Green', size: 'small' },
  },
];

const DEFAULT_PAGE_SIZE = 10;

// Consumer types in display order, and the plural key each maps to in `by_type`.
const TYPE_ORDER = ['workflow', 'agent', 'network', 'widget'];
const TYPE_PLURAL = { workflow: 'workflows', agent: 'agents', network: 'networks', widget: 'widgets' };

// Prototype `.usage-row-ic` / `.usage-row-tag` — type-tinted icon square (10%
// bg · 32% border) and the tiny uppercase tag (12% bg · 30% border). Alpha is
// baked into the arbitrary rgba so Tailwind reliably generates the utility.
const TYPE_VISUAL = {
  workflow: {
    icon: Workflow,
    iconWrap: 'bg-[rgba(43,127,255,0.10)] text-[#2B7FFF] border-[rgba(43,127,255,0.32)]',
    tag: 'bg-[rgba(43,127,255,0.12)] text-[#2B7FFF] border-[rgba(43,127,255,0.30)]',
  },
  agent: {
    icon: Bot,
    iconWrap: 'bg-[rgba(167,139,250,0.10)] text-[#A78BFA] border-[rgba(167,139,250,0.32)]',
    tag: 'bg-[rgba(167,139,250,0.12)] text-[#A78BFA] border-[rgba(167,139,250,0.30)]',
  },
  network: {
    icon: Share2,
    iconWrap: 'bg-[rgba(245,158,11,0.10)] text-[#F59E0B] border-[rgba(245,158,11,0.32)]',
    tag: 'bg-[rgba(245,158,11,0.12)] text-[#F59E0B] border-[rgba(245,158,11,0.30)]',
  },
  widget: {
    icon: LayoutGrid,
    iconWrap: 'bg-[rgba(16,185,129,0.10)] text-[#10B981] border-[rgba(16,185,129,0.32)]',
    tag: 'bg-[rgba(16,185,129,0.12)] text-[#10B981] border-[rgba(16,185,129,0.30)]',
  },
};

// Prototype `.usage-row-action` — a subtle ghost-outline button, not a filled one.
const OPEN_ACTION_CLASS =
  '!h-auto !border !border-edge !bg-transparent !px-2.5 !py-[5px] !text-[11px] !font-semibold !text-content-secondary hover:!border-edge-strong hover:!bg-transparent hover:!text-content';

// Prototype `.usage-row-inst` — subtle accent-tinted chip for the source instance.
const SOURCE_CHIP_CLASS =
  'inline-flex shrink-0 items-center gap-[5px] rounded-lg border border-[rgba(43,127,255,0.24)] bg-[rgba(43,127,255,0.10)] py-[2px] pl-1.5 pr-[7px] text-[10.5px] font-semibold leading-none tracking-[-0.005em] text-[#2B7FFF]';

const UsageConsumerCard = memo(function UsageConsumerCard({ consumer, provider, onOpen, t }) {
  const visual = TYPE_VISUAL[consumer.type] ?? TYPE_VISUAL.workflow;
  const Icon = visual.icon;
  const logoSrc = getIntegrationProviderLogo(provider.slug);

  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3.5 rounded-lg border border-edge bg-white/[0.02] px-3.5 py-[11px] transition-colors hover:border-edge-strong hover:bg-white/[0.035]">
      <span
        aria-hidden="true"
        className={`flex h-9 w-9 items-center justify-center rounded-lg border ${visual.iconWrap}`}
      >
        <Icon size={17} strokeWidth={1.9} />
      </span>

      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold leading-tight text-content">
          {consumer.name}
        </p>
        <div className="mt-[3px] flex flex-wrap items-center gap-2 text-[11px] text-content-muted">
          <span
            className={`rounded-lg border px-[7px] py-[1.5px] text-[9.5px] font-extrabold uppercase leading-none tracking-[0.06em] ${visual.tag}`}
          >
            {t(`integrations.provider.usage.type.${consumer.type}`)}
          </span>
          {consumer.source ? (
            <span className={SOURCE_CHIP_CLASS}>
              {logoSrc ? (
                <img src={logoSrc} alt="" aria-hidden="true" className="h-3 w-3 shrink-0 object-contain" />
              ) : null}
              {t('integrations.provider.usage.source', {
                provider: provider.name,
                channel: consumer.source,
              })}
            </span>
          ) : null}
          <span>{t('integrations.provider.usage.owner', { name: consumer.owner })}</span>
          <span aria-hidden="true">·</span>
          <span>{t('integrations.provider.usage.runs', { count: consumer.uses })}</span>
          <span aria-hidden="true">·</span>
          <span>{t('integrations.provider.usage.lastActivity', { time: consumer.lastActivity })}</span>
        </div>
      </div>

      <Button
        variant="tertiary"
        size="sm"
        rightIcon={ExternalLink}
        className={OPEN_ACTION_CLASS}
        onClick={onOpen}
      >
        {t('integrations.provider.usage.open')}
      </Button>
    </div>
  );
});

/**
 * Provider Detail — Usage tab (CT-4182, admin-only). Mirrors the prototype
 * `detailUsage`: the "Where this integration is used" widget (impact tiles), a
 * Type dropdown filter + search, and a paginated read-only consumer list.
 */
function UsageTab() {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const { provider } = useOutletContext();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const { stats, consumers, isLoading, error } = useProviderUsage(provider.slug, search);

  const handleOpenConsumer = useCallback(() => {
    toast.info({ message: t('integrations.provider.stubs.dataStudio'), canClose: true });
  }, [t, toast]);

  const byType = stats.by_type ?? { workflows: 0, agents: 0, networks: 0, widgets: 0 };

  // `Filters` is memo()'d — keep searchProps and the filter config referentially
  // stable so a keystroke doesn't force it (and its inner useMemos) to re-render.
  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => {
        setSearch(e?.target ? e.target.value : e);
        setPage(1);
      },
      onClear: () => {
        setSearch('');
        setPage(1);
      },
      placeholder: t('integrations.provider.usage.searchPlaceholder'),
    }),
    [search, t],
  );

  const filtersConfig = useMemo(() => {
    const options = TYPE_ORDER.filter((tp) => (byType[TYPE_PLURAL[tp]] ?? 0) > 0).map((tp) => ({
      value: tp,
      label: t(`integrations.provider.usage.type.${tp}`),
    }));
    if (options.length === 0) return [];
    return [
      {
        id: 'type',
        label: t('integrations.provider.usage.filterType'),
        multiple: false,
        value: typeFilter,
        onChange: (next) => {
          setTypeFilter(next);
          setPage(1);
        },
        options,
      },
    ];
  }, [byType, typeFilter, t]);

  const visibleConsumers = useMemo(
    () => (typeFilter.length ? consumers.filter((c) => c.type === typeFilter[0]) : consumers),
    [consumers, typeFilter],
  );

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  const totalPages = Math.max(1, Math.ceil(visibleConsumers.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = visibleConsumers.slice(pageStart, pageStart + perPage);
  const rangeLabel =
    visibleConsumers.length === 0
      ? '0'
      : `${pageStart + 1}–${Math.min(pageStart + perPage, visibleConsumers.length)}`;

  const consumerCount = stats.consumer_count ?? consumers.length;
  const dependLabel =
    consumerCount === 0
      ? t('integrations.provider.usage.noConsumers')
      : t(
          consumerCount === 1
            ? 'integrations.provider.usage.dependOnOne'
            : 'integrations.provider.usage.dependOn',
          { count: consumerCount, name: provider.name },
        );

  return (
    <Card className="overflow-hidden rounded-lg">
      <div className="flex items-center justify-between gap-3 px-[18px] pb-2.5 pt-3.5">
        <h3 className="text-sm font-semibold tracking-[-0.005em] text-content">
          {t('integrations.provider.usage.whereUsed')}
        </h3>
        <span className="text-[11px] font-medium text-content-muted">{dependLabel}</span>
      </div>

      <Card.Body className="px-[18px] pb-[18px] pt-0">
        <div className="mb-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          {IMPACT_TILES.map((tile) => {
            const value = byType[tile.key] ?? 0;
            return (
              <HighlightCard
                key={tile.key}
                variant={tile.variant}
                icon={tile.icon}
                iconHighlight={tile.iconHighlight}
                label={t(`integrations.provider.usage.tiles.${tile.key}`)}
                value={value}
                showUnit={false}
                feedback={t(`integrations.provider.usage.tiles.${tile.helperKey}`)}
                className={value === 0 ? 'opacity-55' : undefined}
              />
            );
          })}
        </div>

        <div className="mb-2.5">
          <Filters searchProps={searchProps} filters={filtersConfig} showAllFilters={false} maxFilters={1} />
        </div>

        {visibleConsumers.length === 0 ? (
          <p className="py-6 text-center text-sm text-content-muted">
            {t(
              search.trim() || typeFilter.length
                ? 'integrations.provider.usage.emptySearch'
                : 'integrations.provider.usage.noConsumers',
            )}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {pageRows.map((consumer) => (
                <UsageConsumerCard
                  key={`${consumer.type}:${consumer.name}`}
                  consumer={consumer}
                  provider={provider}
                  onOpen={handleOpenConsumer}
                  t={t}
                />
              ))}
            </div>
            <div className="mt-3">
              <Pagination
                rangeLabel={rangeLabel}
                totalItems={visibleConsumers.length}
                currentPage={safePage}
                totalPages={totalPages}
                perPage={perPage}
                hasPrevious={safePage > 1}
                hasNext={safePage < totalPages}
                onPrevPage={() => setPage(Math.max(1, safePage - 1))}
                onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
                onPageSizeChange={(size) => {
                  setPerPage(size);
                  setPage(1);
                }}
              />
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default UsageTab;
